import { useState, useEffect, useCallback, useRef } from 'react';
import { Plus, Briefcase, Users, X, Trash2, ChevronRight, Upload, Loader2, CheckCircle, Sparkles, MessageSquare, ChevronDown, MapPin, Monitor, Building2, Wifi, Github, RefreshCw, ArrowRightLeft, Filter, Calendar, User, AlertTriangle, Edit3, Save, Send } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../../contexts/ToastContext';
import api from '@/lib/api';
import { JobTable } from './JobTable';

const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY',
  'LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND',
  'OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY','DC'
];

const WORK_MODES = [
  { value: 'remote',  label: 'Remote',  icon: Wifi,     color: 'text-emerald-600 bg-emerald-50 border-emerald-200', desc: 'Search entire US' },
  { value: 'hybrid',  label: 'Hybrid',  icon: Monitor,  color: 'text-amber-600 bg-amber-50 border-amber-200',   desc: 'Within 100 miles' },
  { value: 'onsite',  label: 'Onsite',  icon: Building2,color: 'text-[#2563eb] bg-blue-50 border-blue-200',     desc: 'Within 100 miles' },
];

const PLATFORMS = [
  { value: 'both',    label: 'Both Platforms',   sub: 'GitHub + Exa AI',                     iconBg: 'bg-[#2563eb]',  Icon: Sparkles },
  { value: 'github',  label: 'GitHub Only',       sub: 'Developer profiles by code',            iconBg: 'bg-gray-900',   Icon: Github },
  { value: 'linkedin',label: 'LinkedIn Only',     sub: 'Exa AI Neural Search',        iconBg: 'bg-[#0A66C2]',  Icon: () => <span className="text-xs font-bold leading-none">in</span> },
];

// Floating platform picker — rendered in a portal to avoid overflow clipping
function PlatformPickerPortal({ anchorEl, onSelect, onClose, selectedPlatform, jobId }) {
  const ref = useRef(null);
  const [pos, setPos] = useState({ top: 0, left: 0 });

  useEffect(() => {
    if (anchorEl) {
      const rect = anchorEl.getBoundingClientRect();
      const pickerWidth = 260;
      let left = rect.left;
      // Keep within viewport
      if (left + pickerWidth > window.innerWidth - 16) {
        left = window.innerWidth - pickerWidth - 16;
      }
      setPos({ top: rect.bottom + 6, left });
    }
  }, [anchorEl]);

  useEffect(() => {
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target) && !anchorEl?.contains(e.target)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [anchorEl, onClose]);

  const [selected, setSelected] = useState(selectedPlatform || 'both');

  if (!anchorEl) return null;

  return (
    <div
      ref={ref}
      style={{ position: 'fixed', top: pos.top, left: pos.left, zIndex: 9999, width: 260 }}
      className="bg-white border border-slate-200 rounded-2xl shadow-2xl p-4"
      data-testid="platform-picker"
    >
      <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-3">Choose Sourcing Platform</p>
      <div className="space-y-2 mb-4">
        {PLATFORMS.map(p => {
          const isSelected = selected === p.value;
          return (
            <button
              key={p.value}
              onClick={() => setSelected(p.value)}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl border transition-all text-left ${
                isSelected ? 'border-[#2563eb] bg-blue-50' : 'border-transparent hover:bg-slate-50'
              }`}
              data-testid={`platform-option-${p.value}`}
            >
              <div className={`w-8 h-8 rounded-xl ${p.iconBg} flex items-center justify-center flex-shrink-0`}>
                <p.Icon className="w-4 h-4 text-white" />
              </div>
              <div className="flex-1">
                <p className={`text-sm font-semibold ${isSelected ? 'text-[#2563eb]' : 'text-slate-700'}`}>{p.label}</p>
                <p className="text-[10px] text-slate-400">{p.sub}</p>
              </div>
              {isSelected && <CheckCircle className="w-4 h-4 text-[#2563eb] flex-shrink-0" />}
            </button>
          );
        })}
      </div>
      <button
        onClick={() => { onSelect(selected); onClose(); }}
        className="w-full bg-[#2563eb] hover:bg-[#1d4ed8] text-white py-2.5 rounded-xl text-sm font-semibold transition-colors flex items-center justify-center gap-2"
        data-testid="confirm-platform-btn"
      >
        <Sparkles className="w-4 h-4" />
        Source on {selected === 'both' ? 'Both' : selected === 'github' ? 'GitHub' : 'LinkedIn'}
      </button>
    </div>
  );
}

export default function JobsPage() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ title: '', jd_text: '', location_city: '', location_state: 'CA', work_mode: 'remote', job_type: '', client: '' });
  const [jdMode, setJdMode] = useState('paste');
  const [jdFile, setJdFile] = useState(null);
  const [extracting, setExtracting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [generatingQsFor, setGeneratingQsFor] = useState(null);
  const [showQuestionsFor, setShowQuestionsFor] = useState(null);
  const [jobQuestions, setJobQuestions] = useState({});
  const [pickerJobId, setPickerJobId] = useState(null);
  const [pickerAnchor, setPickerAnchor] = useState(null);

  // Phase 5 state
  const [statusFilter, setStatusFilter] = useState('all');
  const [ownerFilter, setOwnerFilter]   = useState('all');
  const [filterMembers, setFilterMembers] = useState([]);
  const [clientFilter, setClientFilter] = useState('');   // Phase 6A — filter by client
  const [fromDate, setFromDate]         = useState('');
  const [toDate, setToDate]             = useState('');
  const [showFilters, setShowFilters]   = useState(false);
  const [savingEdit, setSavingEdit]       = useState(false);
  const [transferModal, setTransferModal] = useState(null); // job object
  const [teamMembers, setTeamMembers]     = useState([]);
  const [newOwner, setNewOwner]           = useState('');
  const [transferring, setTransferring]   = useState(false); // {job, candidateCount}
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleting, setDeleting]           = useState(false);
  const [editingJob, setEditingJob]       = useState(null); // job being edited inline
  const [editJdText, setEditJdText]       = useState('');
  const [editJobType, setEditJobType]     = useState('');   // Phase 6A
  const [editClient, setEditClient]       = useState('');   // Phase 6A

  const [repostingId, setRepostingId]     = useState(null);
  const [submissionCounts, setSubmissionCounts] = useState({});
  const navigate = useNavigate();
  const { addToast } = useToast();
  const sourceBtnRefs = useRef({});

  const fetchJobs = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (statusFilter && statusFilter !== 'all') params.append('status', statusFilter);
      if (ownerFilter && ownerFilter !== 'all' && ownerFilter !== 'me') params.append('owned_by', ownerFilter);
      if (clientFilter.trim()) params.append('client', clientFilter.trim());
      if (fromDate) params.append('from_date', fromDate);
      if (toDate)   params.append('to_date', toDate);
      const { data } = await api.get(`/jobs?${params.toString()}`);
      setJobs(data);
    } catch (err) { logger.error(err); }
    finally { setLoading(false); }
  }, [statusFilter, ownerFilter, clientFilter, fromDate, toDate]);

  useEffect(() => { fetchJobs(); }, [fetchJobs]);

  // Fetch pipeline submission counts for all jobs in the current list
  useEffect(() => {
    if (!jobs || jobs.length === 0) return;
    const fetchCounts = async () => {
      const results = await Promise.all(
        jobs.map(job =>
          api.get(`/submissions/pipeline-counts?job_id=${job.job_id}`)
            .then(r => ({ job_id: job.job_id, counts: r.data }))
            .catch(() => ({ job_id: job.job_id, counts: {} }))
        )
      );
      const map = {};
      results.forEach(r => { map[r.job_id] = r.counts; });
      setSubmissionCounts(map);
    };
    fetchCounts();
  }, [jobs]);

  useEffect(() => {
    // Load workspace members for Filter by Owner dropdown
    api.get('/jobs/workspace-members')
      .then(({ data }) => setFilterMembers(data))
      .catch(() => {});
  }, []);

  const openPicker = (e, jobId) => {
    e.stopPropagation();
    if (pickerJobId === jobId) {
      setPickerJobId(null);
      setPickerAnchor(null);
    } else {
      setPickerJobId(jobId);
      setPickerAnchor(e.currentTarget);
    }
  };

  const handlePlatformSelect = (platform) => {
    if (!pickerJobId) return;
    const jid = pickerJobId;
    setPickerJobId(null);
    setPickerAnchor(null);
    navigate(`/jobs/${jid}/candidates?source=${platform}`);
  };

  const handleJdUpload = async (file) => {
    if (!file) return;
    setJdFile(file); setExtracting(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const { data } = await api.post('/jobs/extract-jd', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setForm(prev => ({ ...prev, jd_text: data.text }));
      addToast(`Extracted ${data.char_count?.toLocaleString()} chars from ${file.name}`, 'success');
      setJdMode('paste');
    } catch (err) { addToast('Failed to extract. Try pasting instead.', 'error'); setJdFile(null); }
    finally { setExtracting(false); }
  };

  const createJob = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.jd_text.trim()) { addToast('Title and job description are required', 'error'); return; }
    setSaving(true);
    try {
      await api.post('/jobs', form);
      setShowModal(false);
      setForm({ title: '', jd_text: '', location_city: '', location_state: 'CA', work_mode: 'remote', job_type: '', client: '' });
      setJdFile(null); setJdMode('paste');
      await fetchJobs();
      addToast('Job created! AI is extracting skills from your JD...', 'success');
    } catch (err) {
      const rawDetail = err?.response?.data?.detail;
      let errorMessage = 'Failed to create job';
      if (Array.isArray(rawDetail) && rawDetail.length > 0) {
        errorMessage = rawDetail[0]?.msg || JSON.stringify(rawDetail[0]);
      } else if (typeof rawDetail === 'string') {
        errorMessage = rawDetail;
      }
      addToast(errorMessage, 'error');
    }
    finally { setSaving(false); }
  };

  const generateQuestions = async (jobId) => {
    setGeneratingQsFor(jobId);
    try {
      const { data } = await api.post(`/jobs/${jobId}/generate-questions`);
      setJobQuestions(prev => ({ ...prev, [jobId]: data.questions }));
      setShowQuestionsFor(jobId);
      addToast(`Generated ${data.count} questions!`, 'success');
      fetchJobs();
    } catch (err) { addToast('Failed to generate questions', 'error'); }
    finally { setGeneratingQsFor(null); }
  };

  const loadJobQuestions = async (jobId) => {
    if (jobQuestions[jobId]) { setShowQuestionsFor(showQuestionsFor === jobId ? null : jobId); return; }
    try {
      const { data } = await api.get(`/jobs/${jobId}/questions`);
      setJobQuestions(prev => ({ ...prev, [jobId]: data.questions }));
      setShowQuestionsFor(jobId);
    } catch {}
  };

  const deleteJob = async (jobId) => {
    // Phase 5: soft delete with candidate count warning
    const job = jobs.find(j => j.job_id === jobId);
    if (!job) return;
    const count = job.candidate_count || 0;
    setDeleteConfirm({ job, candidateCount: count });
  };

  const confirmDelete = async () => {
    if (!deleteConfirm) return;
    setDeleting(true);
    try {
      await api.delete(`/jobs/${deleteConfirm.job.job_id}`);
      addToast(`Job archived. ${deleteConfirm.candidateCount} candidates archived.`, 'success');
      setDeleteConfirm(null);
      fetchJobs();
    } catch (err) { addToast(err.response?.data?.detail || 'Delete failed', 'error'); }
    finally { setDeleting(false); }
  };

  const openTransferModal = async (job) => {
    setTransferModal(job);
    setNewOwner('');
    try {
      const { data } = await api.get(`/jobs/${job.job_id}/team-members`);
      setTeamMembers(data);
    } catch { addToast('Could not load team members', 'error'); }
  };

  const confirmTransfer = async () => {
    if (!newOwner || !transferModal) return;
    setTransferring(true);
    try {
      await api.post(`/jobs/${transferModal.job_id}/transfer`, { new_primary_owner: newOwner });
      addToast('Job ownership transferred', 'success');
      setTransferModal(null);
      fetchJobs();
    } catch (err) { addToast(err.response?.data?.detail || 'Transfer failed', 'error'); }
    finally { setTransferring(false); }
  };

  const saveEditJD = async () => {
    if (!editingJob || !editJdText.trim()) return;
    setSavingEdit(true);
    try {
      await api.patch(`/jobs/${editingJob.job_id}`, {
        jd_text:  editJdText,
        job_type: editJobType || null,
        client:   editClient  || null,
      });
      addToast('Job description updated', 'success');
      setEditingJob(null);
      fetchJobs();
    } catch (err) { addToast(err.response?.data?.detail || 'Save failed', 'error'); }
    finally { setSavingEdit(false); }
  };

  const repostJob = async (e, jobId) => {
    e.stopPropagation();
    if (!window.confirm('Repost this job? Old candidates will be archived and a new sourcing round will begin.')) return;
    setRepostingId(jobId);
    try {
      const { data } = await api.post(`/jobs/${jobId}/repost`);
      addToast(data.message || 'Job reposted!', 'success');
      fetchJobs();
    } catch (err) { addToast(err.response?.data?.detail || 'Repost failed', 'error'); }
    finally { setRepostingId(null); }
  };

  const getWorkModeConfig = (wm) => WORK_MODES.find(o => o.value === wm) || WORK_MODES[0];

  return (
    <div className="p-8 bg-gradient-to-br from-slate-50 to-blue-50/30 min-h-full" data-testid="jobs-page" style={{ fontFamily: 'Georgia, serif' }}>
      {/* Platform picker portal */}
      <PlatformPickerPortal
        anchorEl={pickerAnchor}
        onSelect={handlePlatformSelect}
        onClose={() => { setPickerJobId(null); setPickerAnchor(null); }}
        selectedPlatform="both"
        jobId={pickerJobId}
      />

      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-slate-800">Job Roles</h1>
          <p className="text-slate-500 text-sm mt-1">Create positions and source candidates</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setShowFilters(f => !f)}
            className={`flex items-center gap-2 text-sm px-4 py-2.5 rounded-xl border transition-colors ${showFilters || statusFilter !== 'all' || ownerFilter !== 'all' || clientFilter || fromDate || toDate ? 'bg-blue-50 text-[#2563eb] border-blue-200' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'}`}
            data-testid="filter-toggle-btn">
            <Filter className="w-4 h-4" /> Filters
            {(statusFilter !== 'all' || ownerFilter !== 'all' || clientFilter || fromDate || toDate) && <span className="w-2 h-2 rounded-full bg-[#2563eb]" />}
          </button>
          <button data-testid="create-job-btn" onClick={() => setShowModal(true)}
            className="flex items-center gap-2 bg-gradient-to-r from-blue-600 to-emerald-600 text-white text-sm font-semibold px-5 py-2.5 rounded-xl shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all">
            <Plus className="w-4 h-4" /> New Job Role
          </button>
        </div>
      </div>

      {/* Phase 5 — Filter bar */}
      {showFilters && (
        <div className="bg-white border border-slate-200 rounded-xl p-4 mb-5 flex flex-wrap items-end gap-4 shadow-sm" data-testid="jobs-filter-bar">
          {/* Filter by Owner */}
          <div>
            <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider block mb-1">Filter by Owner</label>
            <select value={ownerFilter} onChange={e => setOwnerFilter(e.target.value)}
              className="border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-700 bg-white focus:border-[#2563eb] outline-none min-w-[160px]"
              data-testid="owner-filter-select">
              <option value="all">All Owners</option>
              {filterMembers.map(m => (
                <option key={m.user_id} value={m.user_id}>{m.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider block mb-1">Status</label>
            <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
              className="border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-700 bg-white focus:border-[#2563eb] outline-none"
              data-testid="status-filter-select">
              <option value="all">All Statuses</option>
              <option value="active">Active</option>
              <option value="paused">Paused</option>
              <option value="archived">Archived</option>
            </select>
          </div>
          {/* Phase 6A — Filter by Client */}
          <div>
            <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider block mb-1">Client</label>
            <input
              type="text"
              placeholder="Filter by client..."
              value={clientFilter}
              onChange={e => setClientFilter(e.target.value)}
              className="border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-700 bg-white focus:border-[#2563eb] outline-none w-36"
              data-testid="client-filter-input"
            />
          </div>
          <div>
            <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider block mb-1">From Date</label>
            <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)}
              className="border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-700 bg-white focus:border-[#2563eb] outline-none"
              data-testid="from-date-filter" />
          </div>
          <div>
            <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider block mb-1">To Date</label>
            <input type="date" value={toDate} onChange={e => setToDate(e.target.value)}
              className="border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-700 bg-white focus:border-[#2563eb] outline-none"
              data-testid="to-date-filter" />
          </div>
          {(statusFilter !== 'all' || ownerFilter !== 'all' || clientFilter || fromDate || toDate) && (
            <button onClick={() => { setStatusFilter('all'); setOwnerFilter('all'); setClientFilter(''); setFromDate(''); setToDate(''); }}
              className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-red-500 px-3 py-2 border border-slate-200 rounded-lg hover:bg-red-50 transition-colors">
              <X className="w-3.5 h-3.5" /> Clear
            </button>
          )}
          <span className="text-xs text-slate-400 ml-auto">{jobs.length} job{jobs.length !== 1 ? 's' : ''}</span>
        </div>
      )}

      {/* ── JOB TABLE — JobTable ──────────────────────────────────── */}
      <JobTable
        jobs={jobs}
        loading={loading}
        submissionCounts={submissionCounts}
        onJobClick={(job) => navigate(`/jobs/${job.job_id}/candidates`)}
        onEdit={(job) => { setEditingJob(job); setEditJdText(job.jd_text || ''); setEditJobType(job.job_type || ''); setEditClient(job.client || ''); }}
        onDelete={deleteJob}
        onCopyLink={(job) => {
          const shareUrl = `${window.location.origin}/apply/${job.job_id}`;
          navigator.clipboard?.writeText(shareUrl);
          addToast('Share link copied! Send to candidates.', 'success');
        }}
        onRepost={repostJob}
        onTransfer={openTransferModal}
        onOpenPicker={openPicker}
        repostingId={repostingId}
        pickerJobId={pickerJobId}
        sourceBtnRefs={sourceBtnRefs}
      />

      {/* ── EDIT JD MODAL ──────────────────────────────────────────────────── */}
      {editingJob && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setEditingJob(null)}>
          <div className="bg-white border border-slate-200 rounded-2xl w-full max-w-2xl p-7 shadow-2xl" onClick={e => e.stopPropagation()} data-testid="edit-jd-modal">
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-indigo-50 rounded-xl flex items-center justify-center">
                  <Edit3 className="w-5 h-5 text-indigo-600" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-slate-800">Edit Job Description</h2>
                  <p className="text-xs text-slate-400">{editingJob.title}</p>
                </div>
              </div>
              <button onClick={() => setEditingJob(null)} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
            </div>
            <textarea
              value={editJdText}
              onChange={e => setEditJdText(e.target.value)}
              rows={12}
              className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm text-slate-700 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 outline-none resize-none mb-2"
              placeholder="Job description..."
              data-testid="edit-jd-textarea"
            />
            <p className="text-[10px] text-slate-400 mb-4">{editJdText.length.toLocaleString()} characters · changes will be logged to edit history</p>
            {/* Phase 6A — Internal Tracking in Edit modal */}
            <div className="grid grid-cols-2 gap-3 mb-4 border-t border-dashed border-slate-200 pt-4">
              <div>
                <label className="text-xs font-semibold text-slate-500 block mb-1.5">Job Type <span className="text-slate-300 font-normal">(internal)</span></label>
                <select value={editJobType} onChange={e => setEditJobType(e.target.value)}
                  className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm text-slate-700 bg-white focus:border-indigo-500 outline-none"
                  data-testid="edit-job-type-select">
                  <option value="">No type</option>
                  <option value="W2">W2</option>
                  <option value="C2C">C2C</option>
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-500 block mb-1.5">Client <span className="text-slate-300 font-normal">(internal)</span></label>
                <input type="text" value={editClient} onChange={e => setEditClient(e.target.value)}
                  placeholder="e.g. Oracle Corp"
                  className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm text-slate-700 bg-white focus:border-indigo-500 outline-none"
                  data-testid="edit-client-input" />
              </div>
            </div>
            <div className="flex gap-3">
              <button onClick={() => setEditingJob(null)} className="flex-1 border border-slate-200 text-slate-600 py-3 rounded-xl text-sm font-medium hover:bg-slate-50">Cancel</button>
              <button onClick={saveEditJD} disabled={savingEdit || !editJdText.trim()}
                className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white py-3 rounded-xl text-sm font-semibold disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="save-edit-jd-btn">
                {savingEdit ? <><Loader2 className="w-4 h-4 animate-spin" />Saving...</> : <><Save className="w-4 h-4" />Save Changes</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── TRANSFER MODAL ─────────────────────────────────────────────────── */}
      {transferModal && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setTransferModal(null)}>
          <div className="bg-white border border-slate-200 rounded-2xl w-full max-w-md p-7 shadow-2xl" onClick={e => e.stopPropagation()} data-testid="transfer-modal">
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center">
                  <ArrowRightLeft className="w-5 h-5 text-[#2563eb]" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-slate-800">Transfer Ownership</h2>
                  <p className="text-xs text-slate-400">{transferModal.title}</p>
                </div>
              </div>
              <button onClick={() => setTransferModal(null)} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
            </div>
            <div className="mb-5">
              <label className="text-sm font-semibold text-slate-700 block mb-2">New Primary Owner</label>
              {teamMembers.length === 0 ? (
                <div className="flex items-center gap-2 text-slate-400 text-sm py-4"><Loader2 className="w-4 h-4 animate-spin" /> Loading team members...</div>
              ) : (
                <select value={newOwner} onChange={e => setNewOwner(e.target.value)}
                  className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm text-slate-700 bg-white focus:border-[#2563eb] outline-none"
                  data-testid="transfer-owner-select">
                  <option value="">Select team member...</option>
                  {teamMembers.map(m => (
                    <option key={m.user_id} value={m.user_id}>{m.name} ({m.role})</option>
                  ))}
                </select>
              )}
            </div>
            <div className="flex gap-3">
              <button onClick={() => setTransferModal(null)} className="flex-1 border border-slate-200 text-slate-600 py-3 rounded-xl text-sm font-medium hover:bg-slate-50">Cancel</button>
              <button onClick={confirmTransfer} disabled={!newOwner || transferring}
                className="flex-1 bg-[#2563eb] hover:bg-[#1d4ed8] text-white py-3 rounded-xl text-sm font-semibold disabled:opacity-50"
                data-testid="confirm-transfer-btn">
                {transferring ? <><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Transferring...</> : 'Transfer Ownership'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── DELETE CONFIRM MODAL ────────────────────────────────────────────── */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setDeleteConfirm(null)}>
          <div className="bg-white border border-slate-200 rounded-2xl w-full max-w-md p-7 shadow-2xl" onClick={e => e.stopPropagation()} data-testid="delete-confirm-modal">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 bg-red-50 rounded-xl flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-red-500" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-slate-800">Archive Job?</h2>
                <p className="text-xs text-slate-400">{deleteConfirm.job.title}</p>
              </div>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 mb-5">
              <p className="text-sm text-amber-800">
                This job and <strong>{deleteConfirm.candidateCount} candidate{deleteConfirm.candidateCount !== 1 ? 's' : ''}</strong> will be archived.
                The job will be hidden from the dashboard but data is preserved.
              </p>
            </div>
            <div className="flex gap-3">
              <button onClick={() => setDeleteConfirm(null)} className="flex-1 border border-slate-200 text-slate-600 py-3 rounded-xl text-sm font-medium hover:bg-slate-50">Cancel</button>
              <button onClick={confirmDelete} disabled={deleting}
                className="flex-1 bg-red-500 hover:bg-red-600 text-white py-3 rounded-xl text-sm font-semibold disabled:opacity-50"
                data-testid="confirm-delete-btn">
                {deleting ? <><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Archiving...</> : 'Archive Job'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── CREATE JOB MODAL ──────────────────────────────────────────────────── */}
      {showModal && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowModal(false)}>
          <div className="bg-white/95 backdrop-blur-xl rounded-3xl shadow-[0_20px_60px_-15px_rgba(0,0,0,0.3)] border border-slate-200/60 w-full max-w-2xl max-h-[90vh] overflow-auto" onClick={e => e.stopPropagation()} data-testid="create-job-modal">
            <div className="bg-gradient-to-r from-slate-50 to-white px-6 py-5 border-b border-slate-200/60 flex justify-between items-center sticky top-0 z-10">
              <div>
                <h2 className="text-xl font-bold bg-gradient-to-r from-slate-900 to-slate-700 bg-clip-text text-transparent">New Job Role</h2>
                <p className="text-xs text-slate-400 mt-0.5">AI extracts skills, experience & requirements from your JD automatically</p>
              </div>
              <button onClick={() => setShowModal(false)} className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-xl transition-colors"><X className="w-5 h-5" /></button>
            </div>

            <form onSubmit={createJob} className="space-y-5 p-6">
              {/* Job Title */}
              <div>
                <label className="block text-sm font-bold text-slate-700 mb-2">Job Title <span className="text-red-500">*</span></label>
                <input data-testid="job-title-input" value={form.title} onChange={e => setForm({...form, title: e.target.value})} required
                  className="w-full px-4 py-3.5 border border-slate-200/70 rounded-xl bg-slate-50/50 focus:ring-2 focus:ring-blue-500 outline-none text-slate-900 transition-all hover:bg-white shadow-sm placeholder-slate-400 text-sm"
                  placeholder="e.g. Senior Python Developer, Network Engineer, SAP FIORI Developer..." />
              </div>

              {/* Location + Work Mode */}
              <div className="grid grid-cols-5 gap-3 items-end">
                <div className="col-span-2">
                  <label className="block text-sm font-bold text-slate-700 mb-2">City</label>
                  <input data-testid="job-city-input" value={form.location_city} onChange={e => setForm({...form, location_city: e.target.value})}
                    className="w-full px-4 py-3.5 border border-slate-200/70 rounded-xl bg-slate-50/50 focus:ring-2 focus:ring-blue-500 outline-none text-slate-900 transition-all hover:bg-white shadow-sm placeholder-slate-400 text-sm"
                    placeholder="Los Angeles" />
                </div>
                <div>
                  <label className="block text-sm font-bold text-slate-700 mb-2">State</label>
                  <select data-testid="job-state-select" value={form.location_state} onChange={e => setForm({...form, location_state: e.target.value})}
                    className="w-full px-4 py-3.5 border border-slate-200/70 rounded-xl bg-slate-50/50 focus:ring-2 focus:ring-blue-500 outline-none text-slate-900 transition-all hover:bg-white shadow-sm text-sm">
                    {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div className="col-span-2">
                  <label className="text-slate-700 text-sm font-semibold block mb-1.5">Work Mode</label>
                  <div className="flex gap-1">
                    {WORK_MODES.map(opt => {
                      const Icon = opt.icon;
                      const isSel = form.work_mode === opt.value;
                      return (
                        <button key={opt.value} type="button" data-testid={`work-mode-${opt.value}`}
                          onClick={() => setForm({...form, work_mode: opt.value})}
                          className={`flex-1 flex flex-col items-center gap-0.5 py-2 px-1 rounded-xl border text-xs font-medium transition-all ${isSel ? `${opt.color} border-current font-semibold` : 'border-slate-200 text-slate-500 hover:bg-slate-50'}`}>
                          <Icon className="w-3.5 h-3.5" />{opt.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Location awareness banner */}
              {form.work_mode === 'remote' ? (
                <div className="flex items-center gap-2 p-3 bg-emerald-50 border border-emerald-200 rounded-xl text-xs text-emerald-700">
                  <Wifi className="w-4 h-4 flex-shrink-0" />
                  <span><strong>Remote</strong> — sourcing will search across the <strong>entire United States</strong></span>
                </div>
              ) : form.location_city ? (
                <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-xl text-xs text-amber-700">
                  <MapPin className="w-4 h-4 flex-shrink-0" />
                  <span><strong>{form.work_mode === 'hybrid' ? 'Hybrid' : 'Onsite'}</strong> in {form.location_city}, {form.location_state} — candidates within <strong>100 miles</strong> only</span>
                </div>
              ) : null}

              {/* Job Description */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-slate-700 text-sm font-semibold">
                    Job Description <span className="text-red-500">*</span>
                    <span className="text-slate-400 font-normal text-xs ml-1">— AI extracts everything automatically</span>
                  </label>
                  <div className="flex bg-slate-100 rounded-lg p-0.5">
                    {['paste', 'upload'].map(m => (
                      <button key={m} type="button" onClick={() => setJdMode(m)}
                        className={`px-3 py-1 rounded-md text-xs font-medium transition-all ${jdMode === m ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500'}`}>
                        {m === 'paste' ? 'Paste' : 'Upload File'}
                      </button>
                    ))}
                  </div>
                </div>

                {jdMode === 'upload' ? (
                  <div className="bg-slate-50 border-2 border-dashed border-slate-200 rounded-xl p-6 text-center cursor-pointer hover:border-[#2563eb]/50 transition-all"
                    onClick={() => fileRef.current?.click()} data-testid="jd-upload-zone">
                    <input ref={fileRef} type="file" accept=".pdf,.docx,.txt" className="hidden" onChange={e => handleJdUpload(e.target.files[0])} />
                    {extracting ? <><Loader2 className="w-6 h-6 text-[#2563eb] animate-spin mx-auto" /><p className="text-xs text-slate-500 mt-1">Extracting...</p></> :
                     jdFile ? <><CheckCircle className="w-6 h-6 text-emerald-500 mx-auto" /><p className="text-xs text-emerald-600 mt-1 font-medium">{jdFile.name} ✓</p><p className="text-[10px] text-slate-400">Click to replace</p></> :
                     <><Upload className="w-7 h-7 text-slate-400 mx-auto" /><p className="text-sm text-slate-600 font-medium mt-1">Upload JD file</p><p className="text-[10px] text-slate-400">PDF, DOCX, TXT</p></>}
                  </div>
                ) : (
                  <textarea data-testid="job-jd-input" value={form.jd_text} onChange={e => setForm({...form, jd_text: e.target.value})} required
                    rows={8} className="w-full px-4 py-3.5 border border-slate-200/70 rounded-xl bg-slate-50/50 focus:ring-2 focus:ring-blue-500 outline-none text-slate-900 transition-all hover:bg-white shadow-sm text-sm resize-none placeholder-slate-400"
                    placeholder="Paste the complete job description — responsibilities, requirements, qualifications, years of experience, tech stack. AI will extract and parse everything automatically." />
                )}
                {form.jd_text && <p className="text-[10px] text-slate-400 mt-1">{form.jd_text.length.toLocaleString()} characters</p>}
              </div>

              {/* ─── Internal Tracking (not visible to candidates) ─── */}
              <div className="border-t border-dashed border-slate-200 pt-4 mt-2">
                <p className="text-[10px] text-slate-400 uppercase tracking-wide font-semibold mb-3">🔒 Internal Tracking Only</p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-sm font-semibold text-slate-700 block mb-1.5">Job Type</label>
                    <select value={form.job_type || ''} onChange={e => setForm({...form, job_type: e.target.value})}
                      className="w-full bg-white border border-slate-200 rounded-xl px-4 py-2.5 text-slate-800 focus:border-[#2563eb] focus:ring-2 focus:ring-[#2563eb]/20 outline-none text-sm"
                      data-testid="job-type-select">
                      <option value="">Select Type</option>
                      <option value="W2">W2</option>
                      <option value="C2C">C2C</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-sm font-semibold text-slate-700 block mb-1.5">Client</label>
                    <input type="text" placeholder="e.g. Oracle Corp, Microsoft"
                      value={form.client || ''} onChange={e => setForm({...form, client: e.target.value})}
                      className="w-full bg-white border border-slate-200 rounded-xl px-4 py-2.5 text-slate-800 placeholder-slate-400 focus:border-[#2563eb] focus:ring-2 focus:ring-[#2563eb]/20 outline-none text-sm"
                      data-testid="client-input" />
                  </div>
                </div>
              </div>

              <div className="flex gap-3 pt-1">
                <button type="button" onClick={() => setShowModal(false)} className="px-5 py-2.5 rounded-xl font-medium text-slate-600 hover:bg-slate-200 hover:text-slate-900 transition-colors">Cancel</button>
                <button data-testid="save-job-btn" type="submit" disabled={saving || !form.title.trim() || !form.jd_text.trim()}
                  className="flex-1 bg-gradient-to-r from-blue-600 to-emerald-600 text-white py-2.5 rounded-xl font-bold shadow-lg hover:shadow-xl hover:scale-[1.02] active:scale-[0.98] transition-all disabled:opacity-50 disabled:hover:scale-100">
                  {saving ? 'Creating...' : 'Create Job Role'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
